// ABOUTME: Presents a `workflowStatus` content type to the data-table package as a select of the workflow's states.
// ABOUTME: The column then filters through a picklist of the states and its cells render as the workflow's chips.

import type { WorkflowDefinition } from '@src/_new/modules/workflow';

type WorkflowStatusContentType = {
    type?: string | null;
    workflowCode?: string | null;
};

/**
 * `@ds/data-table` has no `workflowStatus` column: it prints the stored state code and filters it
 * through a free-text search. Handed as a `select` whose options are the states of the column's
 * workflow (option code = state code, label = state label), the column filters through the
 * package's picklist, matching the stored code exactly.
 *
 * Every `workflowStatus` column is marked, whether or not its workflow could be loaded, so the cell
 * renderer recognizes it: the stored content is `select` on some nodes (an offer is created with
 * a `select` status) and `workflowStatus` on others. Without its workflow the column keeps its type
 * and the cells show the raw state code.
 *
 * Read-only on top: a status changes through the workflow transitions, never by editing the cell.
 */
export const asWorkflowStatusGridContentType = <T extends WorkflowStatusContentType>(
    contentType: T,
    workflows: Record<string, WorkflowDefinition>,
    stateLabel: (workflow: WorkflowDefinition, stateCode: string) => string,
    languageIsoCode: string,
): T => {
    if (contentType?.type !== 'workflowStatus') return contentType;
    const workflow = contentType.workflowCode ? workflows[contentType.workflowCode] : undefined;
    if (!workflow) {
        return { ...contentType, readOnly: true, workflowStatusColumn: true };
    }
    return {
        ...contentType,
        type: 'select',
        readOnly: true,
        optionalValues: workflow.states.map(state => ({
            code: state.code,
            value: [{ languageIsoCode, value: stateLabel(workflow, state.code) }],
        })),
        workflowStatusColumn: true,
        workflow,
    };
};

/** Whether a column was a `workflowStatus` one before the translation above. */
export const isWorkflowStatusColumn = (contentType: unknown): boolean =>
    typeof contentType === 'object' &&
    contentType !== null &&
    'workflowStatusColumn' in contentType &&
    contentType.workflowStatusColumn === true;

/** The workflow a translated `workflowStatus` column carries, when it could be loaded. */
export const workflowOfColumn = (contentType: unknown): WorkflowDefinition | undefined =>
    isWorkflowStatusColumn(contentType) &&
    typeof contentType === 'object' &&
    contentType !== null &&
    'workflow' in contentType
        ? (contentType.workflow as WorkflowDefinition)
        : undefined;
